import { axiosBaseQuery } from '@api/api';
import { createApi } from '@reduxjs/toolkit/query/react';

export type ActivityActorType = 'user' | 'pat' | 'system' | 'provider';
export type ActivityCategory = 'channel' | 'bundle' | 'rollout';

export interface ActivityEvent {
  id: string;
  orgId: string;
  projectId: string;
  channelId: string | null;
  channelName: string | null;
  actorType: ActivityActorType;
  actorUserId: string | null;
  actorName: string;
  actorEmail: string | null;
  patName: string | null;
  action: string;
  category: ActivityCategory;
  targetType: string | null;
  targetId: string | null;
  targetName: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface ActivityListResponse {
  items: ActivityEvent[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface GetProjectActivityRequest {
  projectId: string;
  channelId?: string;
  action?: string;
  category?: ActivityCategory;
  actorUserId?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

/**
 * Canonical action constants. These mirror the backend action taxonomy and are
 * the single source of truth for filter options and narrative templates.
 */
export const ACTIVITY_ACTIONS = [
  'channel.created',
  'channel.deleted',
  'channel.production_set',
  'channel.production_unset',
  'channel.managed_publishing_updated',
  'bundle.uploaded',
  'bundle.promoted',
  'bundle.deleted',
  'bundle.expired',
  'rollout.updated',
  'rollout.bundle_revoked',
  'rollout.revocation_cleared',
] as const;

export type ActivityAction = (typeof ACTIVITY_ACTIONS)[number];

export const ACTIVITY_CATEGORIES: ActivityCategory[] = ['channel', 'bundle', 'rollout'];

export const getCategoryForAction = (action: string): ActivityCategory | null => {
  const prefix = action.split('.')[0];
  if (prefix === 'channel' || prefix === 'bundle' || prefix === 'rollout') {
    return prefix;
  }
  return null;
};

export const normalizeActivityListResponse = (response: unknown): ActivityListResponse => {
  if (Array.isArray(response)) {
    return {
      items: response as ActivityEvent[],
      nextCursor: null,
      hasMore: false,
    };
  }

  const typed = response as Partial<ActivityListResponse> | null | undefined;
  return {
    items: Array.isArray(typed?.items) ? typed.items : [],
    nextCursor: typed?.nextCursor ?? null,
    hasMore: Boolean(typed?.hasMore),
  };
};

export const getActivityEventsFromResult = (
  result?: ActivityListResponse | ActivityEvent[] | null
): ActivityEvent[] => {
  if (!result) {
    return [];
  }
  if (Array.isArray(result)) {
    return result;
  }
  return Array.isArray(result.items) ? result.items : [];
};

export const activityApi = createApi({
  reducerPath: 'activityApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Activity'],
  endpoints: builder => ({
    getProjectActivity: builder.query<ActivityListResponse, GetProjectActivityRequest>({
      query: ({ projectId, channelId, action, category, actorUserId, from, to, limit, cursor }) => ({
        url: `/projects/${projectId}/activity`,
        params: { channelId, action, category, actorUserId, from, to, limit, cursor },
      }),
      transformResponse: response => normalizeActivityListResponse(response),
      providesTags: (result, _error, { projectId }) => {
        const items = getActivityEventsFromResult(result);
        return [
          ...items.map(event => ({ type: 'Activity' as const, id: event.id })),
          { type: 'Activity' as const, id: `LIST:${projectId}` },
        ];
      },
    }),
  }),
});

export const { useGetProjectActivityQuery } = activityApi;
